import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { DrawingSettings } from '@shared/types';

export const DEFAULT_DRAWING_SETTINGS: DrawingSettings = {
  locale: 'en',
  companyName: '',
  defaultSheetSize: 'A3',
  approvalChain: [
    { role: 'Drawn by', name: '' },
    { role: 'Checked', name: '' },
    { role: 'Approved', name: '' },
  ],
  defaultTolerances: { general: '±0.5', hole: '±0.1', radius: '±0.05' },
  defaultProjectionMethod: 'third-angle',
  units: 'mm',
};

export function useDrawingSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['drawingSettings', user?.id],
    queryFn: async () => {
      if (!user) return DEFAULT_DRAWING_SETTINGS;
      const ref = doc(firestore, 'userSettings', user.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return DEFAULT_DRAWING_SETTINGS;
      return { ...DEFAULT_DRAWING_SETTINGS, ...snap.data() } as DrawingSettings;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 30, // 30 min — settings rarely change
  });

  const updateSettings = useMutation({
    mutationFn: async (update: Partial<DrawingSettings>) => {
      if (!user) throw new Error('Not authenticated');
      const ref = doc(firestore, 'userSettings', user.id);
      const merged = { ...settings, ...update };
      await setDoc(ref, merged, { merge: true });
      return merged as DrawingSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['drawingSettings', user?.id], data);
    },
  });

  return {
    settings: settings ?? DEFAULT_DRAWING_SETTINGS,
    isLoading,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}
